import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Filter,
  FileText,
  Folder,
  FolderOpen,
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  Send,
  Archive,
  ArchiveRestore,
  Undo2,
  ChevronRight,
  Star,
  Upload,
  File,
  X,
  Book,
  BookOpen,
  HelpCircle,
  Settings,
  Users,
  CreditCard,
  Shield,
  Zap,
  Bell,
  Mail,
  MessageSquare,
  Phone,
  Globe,
  Code,
  Database,
  Server,
  Cloud,
  Lock,
  Key,
  Wrench,
  Lightbulb,
  Target,
  Rocket,
  Heart,
  Gift,
  Award,
  GripVertical,
  type LucideIcon,
} from 'lucide-react';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Available icons for category selection
const CATEGORY_ICONS: { name: string; icon: LucideIcon; label: string }[] = [
  { name: 'folder', icon: Folder, label: 'Folder' },
  { name: 'book', icon: Book, label: 'Book' },
  { name: 'book-open', icon: BookOpen, label: 'Book Open' },
  { name: 'help-circle', icon: HelpCircle, label: 'Help' },
  { name: 'file-text', icon: FileText, label: 'Document' },
  { name: 'settings', icon: Settings, label: 'Settings' },
  { name: 'users', icon: Users, label: 'Users' },
  { name: 'credit-card', icon: CreditCard, label: 'Billing' },
  { name: 'shield', icon: Shield, label: 'Security' },
  { name: 'zap', icon: Zap, label: 'Quick Start' },
  { name: 'bell', icon: Bell, label: 'Notifications' },
  { name: 'mail', icon: Mail, label: 'Email' },
  { name: 'message-square', icon: MessageSquare, label: 'Chat' },
  { name: 'phone', icon: Phone, label: 'Phone' },
  { name: 'globe', icon: Globe, label: 'Web' },
  { name: 'code', icon: Code, label: 'API / Code' },
  { name: 'database', icon: Database, label: 'Database' },
  { name: 'server', icon: Server, label: 'Server' },
  { name: 'cloud', icon: Cloud, label: 'Cloud' },
  { name: 'lock', icon: Lock, label: 'Privacy' },
  { name: 'key', icon: Key, label: 'Authentication' },
  { name: 'wrench', icon: Wrench, label: 'Troubleshooting' },
  { name: 'lightbulb', icon: Lightbulb, label: 'Tips' },
  { name: 'target', icon: Target, label: 'Goals' },
  { name: 'rocket', icon: Rocket, label: 'Getting Started' },
  { name: 'heart', icon: Heart, label: 'Favorites' },
  { name: 'gift', icon: Gift, label: 'Features' },
  { name: 'award', icon: Award, label: 'Best Practices' },
  { name: 'star', icon: Star, label: 'Featured' },
];
import { Pagination, EmptyState, Spinner, Select, useToast } from '@centraoneplus/ui';
import { useKnowledgeStore } from '@stores/knowledgeStore';
import type { ArticleStatus, Category } from '@services/knowledge.service';
import { knowledgeService, type KnowledgeDocument } from '@services/knowledge.service';
import { fileService } from '@services/file.service';
import RagSearch from '@components/knowledge/RagSearch';
import { useBreakpoint } from '@shared/hooks/useBreakpoint';
import {
  knowledgeAnalyticsService,
  type AnalyticsDashboard,
  type ArticleEffectiveness,
  type EmbeddingStatus,
  type FailedSearch,
  type KnowledgeGap,
  type PopularSearch,
} from '@services/knowledge-analytics.service';
import { GapQueue, GAP_FETCH_LIMIT } from './GapQueue';
import {
  ArticleRegisterRow,
  RegisterFoot,
  RegisterHead,
  ReviewBand,
  byLeastRecentlyReviewed,
} from './ArticleRegister';
import { staleArticles, toArticleRow } from './toArticleRow';
import { CategoryRow, CategoryTreeHead } from './CategoryTree';
import {
  DocumentDropZone,
  DocumentRegisterHead,
  DocumentRegisterRow,
} from './DocumentRegister';
import { documentsMeta, toDocumentRow } from './toDocumentRow';
import { CardFoot, CONTROL, KbHeader, KbTabs, PRIMARY, type KbTab } from './shell';
import { CoveragePanels } from './CoveragePanels';
import { InsightsStats } from './InsightsStats';
import {
  ArticlePerformanceSection,
  EmbeddingsSection,
  InsightsHeader,
  InsightsSectionTabs,
  SearchAnalyticsSection,
  sectionUsesPeriod,
  type InsightsSection,
} from './InsightsSections';

const statusColors: Record<ArticleStatus, { variant: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
  draft: { variant: 'default', label: 'Draft' },
  published: { variant: 'success', label: 'Published' },
  archived: { variant: 'warning', label: 'Archived' },
};

type TabType = 'articles' | 'categories' | 'documents' | 'search' | 'analytics';

/**
 * Tab labels, in the order the work happens: write, organise, feed, ask, then
 * find out what is missing. "Ask" and "Insights" keep the names the nav has
 * always used — renaming a tab to match an artboard caption is how a
 * redesign breaks somebody's muscle memory for no gain.
 */
const KB_TAB_LABELS: { id: TabType; label: string }[] = [
  { id: 'articles', label: 'Articles' },
  { id: 'categories', label: 'Categories' },
  { id: 'documents', label: 'Documents' },
  { id: 'search', label: 'Ask' },
  { id: 'analytics', label: 'Insights' },
];

export default function KnowledgeBasePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    articles,
    categories,
    categoryTree,
    total,
    page,
    limit,
    totalPages,
    filters,
    isLoading,
    isLoadingCategories,
    isSaving,
    error,
    statusCounts,
    fetchArticles,
    fetchStatusCounts,
    fetchCategories,
    fetchCategoryTree,
    setFilters,
    setPage,
    setStatusFilter,
    setCategoryFilter,
    publishArticle,
    unpublishArticle,
    archiveArticle,
    updateArticle,
    deleteArticle,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
  } = useKnowledgeStore();

  const tier = useBreakpoint();
  // 12a — per-article embedding state. The report is authoritative and is the
  // only thing that knows about `stale`: an article edited since it was last
  // embedded, where the assistant is still quoting the old text.
  const [embedReports, setEmbedReports] = useState<Map<string, EmbeddingStatus>>(new Map());
  const [isReindexing, setIsReindexing] = useState(false);

  const loadEmbedReports = useCallback(() => {
    knowledgeAnalyticsService
      .getEmbeddingStatus('article')
      .then((rows) => setEmbedReports(new Map(rows.map((r) => [r.sourceId, r]))))
      // No report is not the same as everything being fine: the row then falls
      // back to the article's own embeddingStatus column.
      .catch(() => setEmbedReports(new Map()));
  }, []);

  useEffect(() => {
    loadEmbedReports();
  }, [loadEmbedReports]);

  const articleRows = useMemo(
    // Least recently reviewed first. The default sort IS the screen's opinion
    // about what needs attention, and the header's ↑ has to match it.
    () => articles.map((a) => toArticleRow(a, embedReports)).sort(byLeastRecentlyReviewed),
    [articles, embedReports],
  );
  const stale = useMemo(() => staleArticles(articleRows), [articleRows]);

  // 12c. Both come from endpoints that already exist — `kb_search_logs` is
  // populated, which is why this is the designed gap queue rather than phase
  // 29's fallback coverage report.
  const [gapDashboard, setGapDashboard] = useState<AnalyticsDashboard | null>(null);
  const [gapRows, setGapRows] = useState<KnowledgeGap[]>([]);
  /** The last gap fetch came back full, so the server may have more. */
  const [gapHasMore, setGapHasMore] = useState(false);
  const [loadingMoreGaps, setLoadingMoreGaps] = useState(false);
  /** Why the gap list is empty, when it is empty because the fetch failed. */
  const [gapError, setGapError] = useState<string | null>(null);
  /** The period selector from the old Knowledge Analytics header. */
  const [insightsDays, setInsightsDays] = useState(30);
  const [isRefreshingInsights, setIsRefreshingInsights] = useState(false);

  const loadGaps = useCallback(() => {
    setIsRefreshingInsights(true);
    setGapError(null);
    const dash = knowledgeAnalyticsService
      .getDashboard(insightsDays)
      .then(setGapDashboard)
      .catch(() => setGapDashboard(null));
    const rows = knowledgeAnalyticsService
      .identifyGaps({ limit: GAP_FETCH_LIMIT })
      .then((rows) => {
        setGapRows(rows);
        setGapHasMore(rows.length >= GAP_FETCH_LIMIT);
      })
      // Swallowing this used to set an empty list, which the queue drew as four
      // zeros — indistinguishable from a knowledge base with nothing missing.
      // A rejected request says so instead.
      .catch((err: any) => {
        setGapRows([]);
        setGapHasMore(false);
        const raw = err?.response?.data?.message;
        setGapError(
          (Array.isArray(raw) ? raw.join(', ') : raw) ||
            err?.message ||
            'The request failed.',
        );
      });
    void Promise.all([dash, rows]).finally(() => setIsRefreshingInsights(false));
  }, [insightsDays]);

  useEffect(() => {
    loadGaps();
  }, [loadGaps]);

  // The next batch past the API's 100-per-request ceiling. The offset is the
  // number of rows held now: a dismissal removes the gap on the server too, so
  // the server's list shifts by the same amount and nothing is skipped.
  // Rows already held are not repeated.
  const loadMoreGaps = useCallback(async () => {
    if (loadingMoreGaps) return;
    setLoadingMoreGaps(true);
    try {
      const more = await knowledgeAnalyticsService.identifyGaps({
        limit: GAP_FETCH_LIMIT,
        offset: gapRows.length,
      });
      setGapRows((rows) => {
        const seen = new Set(rows.map((g) => g.id));
        return [...rows, ...more.filter((g) => !seen.has(g.id))];
      });
      setGapHasMore(more.length >= GAP_FETCH_LIMIT);
    } catch (err: any) {
      toast.error('Could not load more gaps', err?.message || 'Please try again.');
    } finally {
      setLoadingMoreGaps(false);
    }
  }, [gapRows.length, loadingMoreGaps, toast]);

  const handleDismissGap = async (gap: KnowledgeGap) => {
    // Optimistic: the row is the user's own decision, and waiting on a round
    // trip to remove it makes the queue feel unresponsive.
    setGapRows((rows) => rows.filter((g) => g.id !== gap.id));
    try {
      await knowledgeAnalyticsService.dismissGap(gap.id, 'Dismissed from the gap queue');
    } catch (err: any) {
      setGapRows((rows) => [gap, ...rows]);
      toast.error('Could not dismiss', err?.message || 'Please try again.');
    }
  };

  // Every number in the subtitle is one we already have. Views are summed
  // across the loaded articles and labelled as a total, NOT as "this month" —
  // nothing records a monthly figure, and inventing the window would be the
  // same lie as inventing the number.
  const totalViews = useMemo(
    () => articleRows.reduce((n, r) => n + r.views, 0),
    [articleRows],
  );

  const [activeTab, setActiveTab] = useState<TabType>('articles');

  // Insights keeps the five sections the old Knowledge Analytics screen had.
  // Each one's data is fetched the first time it is opened rather than all at
  // once on mount: four of the five are never looked at in a given visit, and
  // the gap queue is the only one that has to be ready immediately.
  const [insightsSection, setInsightsSection] = useState<InsightsSection>('overview');
  const [popularSearches, setPopularSearches] = useState<PopularSearch[] | null>(null);
  const [failedSearches, setFailedSearches] = useState<FailedSearch[] | null>(null);
  const [articleStats, setArticleStats] = useState<ArticleEffectiveness[] | null>(null);
  const [embedAll, setEmbedAll] = useState<EmbeddingStatus[] | null>(null);

  useEffect(() => {
    if (activeTab !== 'analytics') return;

    if (insightsSection === 'search' && popularSearches === null) {
      knowledgeAnalyticsService
        .getPopularSearches({ limit: 12 })
        .then(setPopularSearches)
        .catch(() => setPopularSearches([]));
      knowledgeAnalyticsService
        .getFailedSearches({ limit: 12 })
        .then(setFailedSearches)
        .catch(() => setFailedSearches([]));
    }

    if (insightsSection === 'articles' && articleStats === null) {
      knowledgeAnalyticsService
        .getArticleEffectiveness()
        .then(setArticleStats)
        .catch(() => setArticleStats([]));
    }

    // No source type: this section is about everything the assistant reads,
    // and a documents-shaped hole in it would be invisible.
    if (insightsSection === 'embeddings' && embedAll === null) {
      knowledgeAnalyticsService
        .getEmbeddingStatus()
        .then(setEmbedAll)
        .catch(() => setEmbedAll([]));
    }
  }, [activeTab, insightsSection, popularSearches, articleStats, embedAll]);

  /** Queue sources for re-embedding, then re-read the report so the list moves. */
  const handleReindex = async (sourceIds: string[]) => {
    if (sourceIds.length === 0) return;
    setIsReindexing(true);
    try {
      await knowledgeAnalyticsService.reindex(sourceIds);
      toast.success('Queued', `${sourceIds.length} source(s) will be re-read shortly.`);
      const rows = await knowledgeAnalyticsService.getEmbeddingStatus();
      setEmbedAll(rows);
      loadEmbedReports();
    } catch (err) {
      console.error('Failed to queue re-embedding:', err);
      toast.error('Could not queue re-embedding', 'The assistant is still using the previous text.');
    } finally {
      setIsReindexing(false);
    }
  };
  const [search, setSearch] = useState(filters.search || '');
  const [showFilters, setShowFilters] = useState(false);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);

  // Category management state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    parentId: '',
    icon: '',
    isPublic: true,
  });
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Drag and drop state for category reordering
  const [draggedCategory, setDraggedCategory] = useState<Category | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<string | null>(null);

  // Search suggestions state
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);

  // Document state
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);

  // 18f. The register renders rows; the page keeps the service objects so the
  // open / download / category verbs still act on the real document.
  const documentById = useMemo(
    () => new Map(documents.map((d) => [d.id, d])),
    [documents],
  );
  const documentRows = useMemo(
    () =>
      documents.map((d) =>
        toDocumentRow(d, categories.find((c) => c.id === d.categoryId)?.name ?? null),
      ),
    [documents, categories],
  );
  // Total document count for the tab badge — kept independent of the documents-tab
  // category filter so it stays accurate on every tab (#9105).
  const [documentCount, setDocumentCount] = useState(0);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [docCategoryFilter, setDocCategoryFilter] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== filters.search) {
        setFilters({ ...filters, search, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch search suggestions
  useEffect(() => {
    if (search.length < 2) {
      setSearchSuggestions([]);
      setShowSearchSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const suggestions = await knowledgeService.getSuggestions(search, 5);
        setSearchSuggestions(suggestions);
        setShowSearchSuggestions(suggestions.length > 0);
      } catch {
        // Ignore errors for suggestions
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [search]);

  // Initial fetch
  useEffect(() => {
    fetchArticles();
    fetchStatusCounts();
    fetchCategories();
    fetchCategoryTree();
  }, []);

  // Fetch documents on mount so the Documents tab count badge is accurate on
  // every tab (#9105), and refetch when the documents-tab category filter changes.
  useEffect(() => {
    fetchDocuments();
  }, [docCategoryFilter]);

  const fetchDocuments = async () => {
    setIsLoadingDocuments(true);
    try {
      const docs = await knowledgeService.getDocuments(docCategoryFilter || undefined);
      setDocuments(docs);
      // Only an unfiltered fetch reflects the true total used by the tab badge.
      if (!docCategoryFilter) setDocumentCount(docs.length);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      toast.error('Error', 'Failed to load documents');
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const pdfFiles = fileArray.filter(f => f.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      toast.error('Invalid file type', 'Please upload PDF files only');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < pdfFiles.length; i++) {
        const file = pdfFiles[i];
        await knowledgeService.uploadDocument(file, (progress) => {
          setUploadProgress(Math.round(((i + progress / 100) / pdfFiles.length) * 100));
        });
      }
      toast.success('Upload complete', `${pdfFiles.length} document(s) uploaded successfully`);
      fetchDocuments();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Upload failed', 'Failed to upload document(s)');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleChangeDocumentCategory = async (id: string, categoryId: string) => {
    try {
      await knowledgeService.updateDocumentCategory(id, categoryId || null);
      toast.success('Updated', 'Document category updated');
      fetchDocuments();
    } catch (err) {
      console.error('Failed to update document category:', err);
      toast.error('Error', 'Failed to update category');
    }
  };

  const handleSelectDocument = (id: string, checked: boolean) => {
    setSelectedDocuments((prev) => (checked ? [...prev, id] : prev.filter((d) => d !== id)));
  };

  const handleSelectAllDocuments = (checked: boolean) => {
    setSelectedDocuments(checked ? documents.map((d) => d.id) : []);
  };

  const handleBulkSetDocumentCategory = async (categoryId: string) => {
    try {
      await Promise.all(
        selectedDocuments.map((id) => knowledgeService.updateDocumentCategory(id, categoryId || null)),
      );
      toast.success('Updated', `Category set for ${selectedDocuments.length} document(s)`);
      setSelectedDocuments([]);
      fetchDocuments();
    } catch (err) {
      console.error('Failed to bulk set document category:', err);
      toast.error('Error', 'Failed to set category');
    }
  };

  /**
   * Open the original file in a new tab.
   *
   * The window is opened synchronously inside the click gesture so it is not
   * popup-blocked, then navigated once the presigned URL resolves. Calling
   * window.open() AFTER awaiting the URL lands on a blank tab that never
   * navigates (bug #9099). `noopener` is omitted because we need the handle to
   * navigate it, and the opener is nulled instead to still prevent
   * reverse-tabnabbing.
   */
  const openDocumentInTab = (doc: KnowledgeDocument) => {
    const popup = window.open('', '_blank');
    if (popup) {
      try { popup.opener = null; } catch { /* ignore */ }
      try { popup.document.write('Loading document…'); } catch { /* ignore */ }
    }
    void (async () => {
      try {
        let viewUrl = doc.url;
        if (!viewUrl) {
          const result = await knowledgeService.getDocumentDownloadUrl(doc.id);
          viewUrl = result.url;
        }
        if (!viewUrl) {
          popup?.close();
          toast.error('Error', 'Document URL not available');
          return;
        }
        if (popup) {
          popup.location.href = viewUrl;
        } else {
          toast.error('Popup blocked', 'Please allow popups to view the document.');
        }
      } catch (err) {
        popup?.close();
        console.error('Failed to get document URL:', err);
        toast.error('Error', 'Failed to open document');
      }
    })();
  };

  /**
   * Save the file.
   *
   * AC3 (#9100): request the presigned URL via /download-url and save it as a
   * same-origin blob so the filename is preserved and the deprecated /download
   * path is avoided. Falls back to the authenticated same-origin stream if the
   * presigned fetch fails (e.g. CORS).
   */
  const downloadDocument = async (doc: KnowledgeDocument) => {
    const filename = doc.filename || doc.title || 'document.pdf';
    try {
      const { url } = await knowledgeService.getDocumentDownloadUrl(doc.id);
      if (!url) throw new Error('No download URL returned');
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Download fetch failed: ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Presigned download failed, falling back to authenticated stream:', err);
      try {
        await fileService.downloadAttachment({
          url: `/api/knowledge/documents/${doc.id}/download`,
          name: filename,
        });
      } catch (err2) {
        console.error('Failed to download document:', err2);
        toast.error('Error', 'Failed to download document');
      }
    }
  };

  /**
   * The header's one line of standing facts, for the tab you are on.
   *
   * Each tab answers a different question, so a single global line would be
   * wrong on four screens out of five. Counts that are still loading are
   * omitted rather than rendered as zero.
   */
  const headerMeta = useMemo(() => {
    switch (activeTab) {
      case 'categories':
        return `${categories.length} categories · drag to reorder or re-parent`;
      case 'documents':
        return documentsMeta(documentRows, documentCount || documentRows.length);
      case 'search':
        return 'Answers drawn from your articles and documents';
      case 'analytics':
        return gapRows.length > 0
          ? `${gapRows.length} open ${gapRows.length === 1 ? 'gap' : 'gaps'} · what people asked for and did not get`
          : 'What people asked for and did not get';
      default:
        return [
          `${total || articleRows.length} articles`,
          `${totalViews.toLocaleString()} views`,
          `${documentCount} documents`,
        ].join(' · ');
    }
  }, [
    activeTab,
    categories.length,
    documentRows,
    documentCount,
    gapRows.length,
    total,
    articleRows.length,
    totalViews,
  ]);

  const KB_TABS = useMemo<KbTab<TabType>[]>(
    () =>
      KB_TAB_LABELS.map((tab) => ({
        ...tab,
        count:
          tab.id === 'articles'
            ? total || articleRows.length
            : tab.id === 'categories'
              ? categories.length
              : tab.id === 'documents'
                ? documentCount
                : tab.id === 'analytics' && gapRows.length > 0
                  ? gapRows.length
                  : undefined,
      })),
    [total, articleRows.length, categories.length, documentCount, gapRows.length],
  );

  const handleDeleteDocument = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await knowledgeService.deleteDocument(id);
      toast.success('Deleted', 'Document deleted successfully');
      fetchDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
      toast.error('Error', 'Failed to delete document');
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedArticles(articles.map((a) => a.id));
    } else {
      setSelectedArticles([]);
    }
  };

  const handleSelectArticle = (articleId: string, checked: boolean) => {
    if (checked) {
      setSelectedArticles([...selectedArticles, articleId]);
    } else {
      setSelectedArticles(selectedArticles.filter((id) => id !== articleId));
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await publishArticle(id);
      setActionMenuOpen(null);
      toast.success('Article published');
    } catch (err) {
      console.error('Failed to publish:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Publish failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to publish article');
    }
  };

  const handleUnpublish = async (id: string) => {
    try {
      await unpublishArticle(id);
      setActionMenuOpen(null);
      toast.success('Article unpublished');
    } catch (err) {
      console.error('Failed to unpublish:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Unpublish failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to unpublish article');
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveArticle(id);
      setActionMenuOpen(null);
      toast.success('Article archived');
    } catch (err) {
      console.error('Failed to archive:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Archive failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to archive article');
    }
  };

  // Restore an archived article back to Draft. There is no dedicated
  // unarchive endpoint; the update endpoint accepts a status transition,
  // and the author can re-publish from Draft afterwards.
  const handleUnarchive = async (id: string) => {
    try {
      await updateArticle(id, { status: 'draft' });
      setActionMenuOpen(null);
      toast.success('Article unarchived');
    } catch (err) {
      console.error('Failed to unarchive:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Update failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to unarchive article');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this article?')) return;
    try {
      await deleteArticle(id);
      setActionMenuOpen(null);
      toast.success('Article deleted');
    } catch (err) {
      console.error('Failed to delete:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Delete failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to delete article');
    }
  };

  // Bulk action handlers
  const handleBulkPublish = async () => {
    try {
      await Promise.all(selectedArticles.map((id) => publishArticle(id)));
      setSelectedArticles([]);
      toast.success('Articles published');
    } catch (err) {
      console.error('Failed to bulk publish:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Publish failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to publish articles');
    }
  };

  const handleBulkUnpublish = async () => {
    try {
      await Promise.all(selectedArticles.map((id) => unpublishArticle(id)));
      setSelectedArticles([]);
      toast.success('Articles unpublished');
    } catch (err) {
      console.error('Failed to bulk unpublish:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Unpublish failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to unpublish articles');
    }
  };

  const handleBulkArchive = async () => {
    try {
      await Promise.all(selectedArticles.map((id) => archiveArticle(id)));
      setSelectedArticles([]);
      toast.success('Articles archived');
    } catch (err) {
      console.error('Failed to bulk archive:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Archive failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to archive articles');
    }
  };

  const handleBulkSetCategory = async (categoryId: string | null) => {
    try {
      await Promise.all(selectedArticles.map((id) => updateArticle(id, { categoryId })));
      toast.success('Updated', `Category set for ${selectedArticles.length} article(s)`);
      setSelectedArticles([]);
      fetchArticles();
    } catch (err) {
      console.error('Failed to bulk set category:', err);
      toast.error('Error', 'Failed to set category');
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedArticles.length} article(s)?`)) return;
    try {
      await Promise.all(selectedArticles.map((id) => deleteArticle(id)));
      setSelectedArticles([]);
      toast.success('Articles deleted');
    } catch (err) {
      console.error('Failed to bulk delete:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Delete failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to delete articles');
    }
  };

  // Category management
  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, {
          name: categoryForm.name,
          description: categoryForm.description || undefined,
          parentId: categoryForm.parentId || undefined,
          icon: categoryForm.icon || undefined,
          isPublic: categoryForm.isPublic,
        });
        toast.success('Category updated');
      } else {
        await createCategory({
          name: categoryForm.name,
          description: categoryForm.description || undefined,
          parentId: categoryForm.parentId || undefined,
          icon: categoryForm.icon || undefined,
          isPublic: categoryForm.isPublic,
        });
        toast.success('Category created');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', parentId: '', icon: '', isPublic: true });
    } catch (err) {
      // Surface a clear message instead of failing silently (#9101). A duplicate
      // name returns 409; show a friendly toast and keep the modal open with the
      // entered value so the user can correct it.
      const e = err as {
        response?: { status?: number; data?: { message?: string; error?: string } };
        message?: string;
      };
      const status = e?.response?.status;
      const apiMsg = e?.response?.data?.message || e?.response?.data?.error;
      const isDuplicate =
        status === 409 || /already exists|duplicate/i.test(String(apiMsg ?? ''));
      if (isDuplicate) {
        toast.error(
          'Duplicate category',
          'A category with this name already exists. Please use a different name.'
        );
      } else {
        toast.error('Save failed', apiMsg || (err instanceof Error ? err.message : 'Failed to save category'));
      }
      console.error('Failed to save category:', err);
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      parentId: category.parentId || '',
      icon: category.icon || '',
      isPublic: category.isPublic,
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category? Articles in this category will become uncategorized.')) return;
    try {
      await deleteCategory(id);
      toast.success('Category deleted');
    } catch (err) {
      console.error('Failed to delete category:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Delete failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to delete category');
    }
  };

  const toggleCategoryExpanded = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Drag and drop handlers for category reordering
  const handleCategoryDragStart = (e: React.DragEvent, category: Category) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', category.id);
    setDraggedCategory(category);
  };

  const handleCategoryDragOver = (e: React.DragEvent, categoryId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedCategory && draggedCategory.id !== categoryId) {
      setDragOverCategory(categoryId);
    }
  };

  const handleCategoryDragLeave = () => {
    setDragOverCategory(null);
  };

  const handleCategoryDragEnd = () => {
    setDraggedCategory(null);
    setDragOverCategory(null);
  };

  const handleCategoryDrop = async (e: React.DragEvent, targetCategory: Category) => {
    e.preventDefault();
    setDragOverCategory(null);

    if (!draggedCategory || draggedCategory.id === targetCategory.id) {
      setDraggedCategory(null);
      return;
    }

    // Only allow reordering within the same parent level
    if (draggedCategory.parentId !== targetCategory.parentId) {
      toast.error('Cannot reorder', 'Categories can only be reordered within the same level');
      setDraggedCategory(null);
      return;
    }

    try {
      // Get siblings at the same level
      const siblings = draggedCategory.parentId
        ? categories.filter(c => c.parentId === draggedCategory.parentId)
        : categoryTree.filter(c => !c.parentId);

      // Calculate new sort orders
      const draggedIndex = siblings.findIndex(c => c.id === draggedCategory.id);
      const targetIndex = siblings.findIndex(c => c.id === targetCategory.id);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedCategory(null);
        return;
      }

      // Create new order by moving dragged item to target position
      const reorderedSiblings = [...siblings];
      const [removed] = reorderedSiblings.splice(draggedIndex, 1);
      reorderedSiblings.splice(targetIndex, 0, removed);

      // Create sort order updates
      const updates = reorderedSiblings.map((cat, index) => ({
        id: cat.id,
        sortOrder: index,
      }));

      await reorderCategories(updates);
      toast.success('Reordered', 'Category order updated');
    } catch (err) {
      console.error('Failed to reorder categories:', err);
      toast.error('Error', 'Failed to reorder categories');
    }

    setDraggedCategory(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatRelativeTime = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  const clearFilters = () => {
    setSearch('');
    setFilters({});
    setSearchSuggestions([]);
    setShowSearchSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearch(suggestion);
    setShowSearchSuggestions(false);
    setSearchSuggestions([]);
  };

  const hasActiveFilters = search || Object.values(filters).some((v) => v);

  // Render category tree
  // 12e. The row is presentational (CategoryTree.tsx); every handler below is
  // the one that already shipped, so reorder, re-parent, expand, edit and
  // delete behave exactly as before.
  const renderCategoryTree = (cats: Category[], level = 0) => {
    return cats.map((cat) => {
      const hasChildren = !!cat.children && cat.children.length > 0;
      const isExpanded = expandedCategories.has(cat.id);
      const iconConfig = cat.icon ? CATEGORY_ICONS.find((i) => i.name === cat.icon) : null;
      const IconComponent = iconConfig?.icon ?? Folder;
      return (
        <div key={cat.id}>
          <CategoryRow
            cat={cat}
            level={level}
            expanded={isExpanded}
            hasChildren={hasChildren}
            dragging={draggedCategory?.id === cat.id}
            dragOver={dragOverCategory === cat.id}
            icon={<IconComponent className="h-3.5 w-3.5" />}
            onToggleExpand={() => toggleCategoryExpanded(cat.id)}
            onSelect={() => setCategoryFilter(filters.categoryId === cat.id ? undefined : cat.id)}
            onEdit={() => handleEditCategory(cat)}
            onDelete={() => handleDeleteCategory(cat.id)}
            dragHandlers={{
              draggable: true,
              onDragStart: (e) => handleCategoryDragStart(e as any, cat),
              onDragOver: (e) => handleCategoryDragOver(e as any, cat.id),
              onDragLeave: handleCategoryDragLeave,
              onDragEnd: handleCategoryDragEnd,
              onDrop: (e) => handleCategoryDrop(e as any, cat),
            }}
          />
          {hasChildren && isExpanded && renderCategoryTree(cat.children!, level + 1)}
        </div>
      );
    });
  };

  return (
    <div className="py-4 pr-4 space-y-[14px]">
      {/* 18a–18f. One header card and one tab strip for all six screens: the
          frame was six copies of the same markup, each drifting slightly. The
          meta line answers "what is in here" for the tab you are on, because
          that is the question the counts are for. */}
      <KbHeader
        title="Knowledge Base"
        meta={headerMeta}
        actions={
          <>
            {activeTab === 'articles' && (
              <button
                type="button"
                onClick={() => navigate('/knowledge/new')}
                className={PRIMARY}
              >
                <Plus className="h-4 w-4" />
                New Article
              </button>
            )}
            {activeTab === 'categories' && (
              <button
                type="button"
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryForm({ name: '', description: '', parentId: '', icon: '', isPublic: true });
                  setShowCategoryModal(true);
                }}
                className={PRIMARY}
              >
                <Plus className="h-4 w-4" />
                New Category
              </button>
            )}
            {activeTab === 'documents' && (
              <label className={`${PRIMARY} cursor-pointer`}>
                <Upload className="h-4 w-4" />
                Upload PDF
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  className="hidden"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  disabled={isUploading}
                />
              </label>
            )}
          </>
        }
        tabs={
          <KbTabs
            tabs={KB_TABS}
            active={activeTab}
            onSelect={setActiveTab}
            aside={
              /* The sort control names the order the table is actually in —
                 the shipped screen labelled a column `↑` while ordering rows
                 by something else. */
              activeTab === 'articles' ? (
                <span className={CONTROL}>
                  <span className="font-mono text-[9.5px] uppercase tracking-[.14em] text-[var(--co-ink-4)]">
                    Sort
                  </span>
                  Least recently reviewed
                </span>
              ) : undefined
            }
          />
        }
      />

      {activeTab === 'articles' ? (
        <>
          {/* The three status cards are gone (phase 27 §6). Two of them read
              zero, status is already a column, and they cost ~90px of the
              vertical space the table needed. A status filter belongs in the
              filter row, not in three cards that mostly say nothing. */}

          {/* Search and Filters */}
          <div
            className="rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] p-4"
            style={{ boxShadow: 'var(--co-shadow-card)' }}
          >
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search with suggestions */}
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--co-ink-4)]" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => searchSuggestions.length > 0 && setShowSearchSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
                  className="h-[34px] w-full rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] pl-10 pr-9 text-[12.5px] text-[var(--co-ink)] placeholder-[var(--co-ink-4)] focus:border-[var(--co-brass)] focus:outline-none"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSearchSuggestions([]);
                      setShowSearchSuggestions(false);
                    }}
                    aria-label="Clear search"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                {/* Search Suggestions Dropdown */}
                {showSearchSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                    {searchSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
                      >
                        <Search className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <span className="truncate">{suggestion}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* A select-SHAPED button, never a native <select>: the browser's
                  own dropdown arrives in its own blue, which on a screen whose
                  premise is that colour means something is the only chroma
                  present. */}
              <div className="relative flex-none">
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={categoryMenuOpen}
                  onClick={() => setCategoryMenuOpen((v) => !v)}
                  className="flex h-[34px] w-48 items-center justify-between gap-[7px] rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-[11px] text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-ink)]"
                >
                  <span className="truncate">
                    {categories.find((c) => c.id === filters.categoryId)?.name ?? 'All categories'}
                  </span>
                  <span className="flex-none text-[var(--co-ink-4)]" aria-hidden="true">
                    ▾
                  </span>
                </button>
                {categoryMenuOpen && (
                  <ul
                    role="listbox"
                    className="absolute left-0 right-0 top-9 z-20 max-h-[260px] overflow-y-auto rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)]"
                  >
                    {[{ id: '', name: 'All categories' }, ...categories].map((cat) => {
                      const on = (filters.categoryId ?? '') === cat.id;
                      return (
                        <li key={cat.id || '__all'}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={on}
                            onClick={() => {
                              setCategoryFilter(cat.id || undefined);
                              setCategoryMenuOpen(false);
                            }}
                            className="flex h-[30px] w-full items-center truncate px-3 text-left text-[12.5px] text-[var(--co-ink)] hover:bg-[var(--co-selected)]"
                            style={{
                              fontWeight: on ? 600 : 400,
                              background: on ? 'var(--co-selected)' : undefined,
                            }}
                          >
                            {cat.name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={CONTROL}
                style={
                  showFilters
                    ? { borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }
                    : undefined
                }
              >
                <Filter className="h-4 w-4" />
                Filters
              </button>
            </div>

            {/* Extended Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <ThemedSelect
                      value={filters.status || ''}
                      onChange={(e) => setStatusFilter((e.target.value as ArticleStatus) || undefined)}
                      className="input w-full"
                    >
                      <option value="">All statuses</option>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </ThemedSelect>
                  </div>
                </div>

                {hasActiveFilters && (
                  <div className="mt-4 flex items-center justify-end">
                    <button onClick={clearFilters} className="text-sm text-primary-600 hover:text-primary-700">
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bulk Actions */}
          {selectedArticles.length > 0 && (
            <div
              className="rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] p-4"
              style={{ boxShadow: 'var(--co-shadow-card)' }}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                  {`${selectedArticles.length} selected`}
                </span>
                <div className="flex items-center gap-2">
                  <ThemedSelect
                    value=""
                    onChange={(e) => e.target.value && handleBulkSetCategory(e.target.value === '__clear__' ? null : e.target.value)}
                    className="btn btn-outline btn-sm"
                  >
                    <option value="">Set category</option>
                    <option value="__clear__">No category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </ThemedSelect>
                  <button className="btn btn-outline btn-sm" onClick={handleBulkPublish}>Publish</button>
                  <button className="btn btn-outline btn-sm" onClick={handleBulkUnpublish}>Unpublish</button>
                  <button className="btn btn-outline btn-sm" onClick={handleBulkArchive}>Archive</button>
                  <button className="btn btn-outline btn-sm text-red-600 hover:bg-red-50" onClick={handleBulkDelete}>Delete</button>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div
              className="rounded-[10px] border px-[18px] py-[14px]"
              style={{
                borderColor: 'var(--co-burgundy-line)',
                background: 'var(--co-burgundy-wash)',
              }}
            >
              <p className="text-[12.5px] leading-[1.55] text-[var(--co-burgundy)]">{error}</p>
            </div>
          )}

          {/* Articles Table —
              `overflow-visible` (not `overflow-hidden`) so the row 3-dot
              dropdown menu can extend past the card's rounded corners
              instead of being clipped. The table content itself doesn't
              bleed past the rounded edges, so visual rounding still looks
              correct without the clip. */}
          <div
            className="overflow-visible rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)]"
            style={{ boxShadow: 'var(--co-shadow-card)' }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <Spinner size="lg" />
              </div>
            ) : articles.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-12 w-12" />}
                title="No articles found"
                description={
                  hasActiveFilters
                    ? 'Try adjusting your filters'
                    : 'Create your first article to get started'
                }
                action={
                  hasActiveFilters ? undefined : (
                    <button onClick={() => navigate('/knowledge/new')} className="btn btn-primary">
                      Create Article
                    </button>
                  )
                }
              />
            ) : (
              /* Artboard 16a. LAST REVIEWED is the default sort and the band's
                 source; VIEWS carries the weight helpfulness would have. The
                 band names the article when there is one and does not render
                 when nothing is stale. */
              <div role="table" aria-label="Articles">
                <ReviewBand stale={stale} onReview={(id) => navigate(`/knowledge/${id}`)} />
                <RegisterHead
                  tier={tier}
                  allSelected={selectedArticles.length > 0 && selectedArticles.length === articles.length}
                  onSelectAllOnPage={handleSelectAll}
                />
                <div role="rowgroup">
                  {articleRows.map((row) => (
                    <ArticleRegisterRow
                      key={row.id}
                      row={row}
                      tier={tier}
                      selected={selectedArticles.includes(row.id)}
                      onToggle={(checked) => handleSelectArticle(row.id, checked)}
                      onOpen={() => navigate(`/knowledge/${row.id}`)}
                      onMenu={() => setActionMenuOpen(actionMenuOpen === row.id ? null : row.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* At one page, `Showing 1 to 4 of 4` plus prev / 1 / next is four
                controls for no navigation. The foot says what is shown and
                what is missing instead; pagination returns at page two. */}
            {articles.length > 0 &&
              (totalPages > 1 ? (
                <div className="border-t border-[var(--co-line-frame)] px-4 py-3">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    totalItems={total}
                    pageSize={limit}
                  />
                </div>
              ) : (
                <RegisterFoot shown={articles.length} total={total || articles.length} />
              ))}
          </div>
        </>
      ) : activeTab === 'categories' ? (
        /* 18e. The heading moved into the shared header card; what is left is
           the tree and one true sentence about what dragging does. */
        <div
          className="overflow-hidden rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)]"
          style={{ boxShadow: 'var(--co-shadow-card)' }}
        >
          {isLoadingCategories ? (
            <div className="flex items-center justify-center p-12">
              <Spinner size="lg" />
            </div>
          ) : categoryTree.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <h3 className="text-[15px] font-semibold text-[var(--co-ink)]">No categories</h3>
              <p className="mx-auto mt-2 max-w-[56ch] text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">
                Categories are the shelves the help centre is arranged on. Until one exists every
                article is filed as uncategorised.
              </p>
              <div className="mt-5 flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(true)}
                  className={PRIMARY}
                >
                  <Plus className="h-4 w-4" />
                  Create Category
                </button>
              </div>
            </div>
          ) : (
            <div role="table" aria-label="Categories">
              <CategoryTreeHead />
              <div role="rowgroup">{renderCategoryTree(categoryTree)}</div>
              {/* The artboard's foot reads "re-parenting rewrites descendant
                  slug paths". Neither half is true here: dragging refuses a
                  drop across levels, and slugs are independent of the tree, so
                  moving a parent rewrites nothing. This says what the controls
                  actually do. */}
              <CardFoot
                left={`${categories.length} categories`}
                note="Dragging reorders within a level. To move a category under a different parent, edit it and change its parent."
              />
            </div>
          )}
        </div>
      ) : activeTab === 'documents' ? (
        /* Documents Tab */
        <>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-4)]">
              Showing
            </span>
            <div className="w-52">
              <Select
                value={docCategoryFilter}
                onChange={(e) => setDocCategoryFilter(e.target.value)}
                placeholder="All categories"
                options={[
                  { value: '', label: 'All categories' },
                  ...categories.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            </div>
          </div>

          {/* 18f. The dropzone keeps every existing upload path — drag, drop
              and the header's file input all call handleFileUpload. */}
          <DocumentDropZone
            dragging={isDragOver}
            uploading={isUploading}
            progress={uploadProgress}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onBrowse={
              <label className="cursor-pointer font-semibold text-[var(--co-brass-ink)] underline underline-offset-2">
                browse
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  className="hidden"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  disabled={isUploading}
                />
              </label>
            }
          />

          {selectedDocuments.length > 0 && (
            <div
              className="flex items-center gap-3 rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-5 py-3"
              style={{ boxShadow: 'var(--co-shadow-card)' }}
            >
              <span className="font-mono text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                {`${selectedDocuments.length} selected`}
              </span>
              <div className="flex-1" />
              <div className="w-52">
                <Select
                  value=""
                  onChange={(e) =>
                    e.target.value &&
                    handleBulkSetDocumentCategory(e.target.value === '__clear__' ? '' : e.target.value)
                  }
                  placeholder="Set category"
                  options={[
                    { value: '__clear__', label: 'No category' },
                    ...categories.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                />
              </div>
            </div>
          )}

          <div
            className="overflow-hidden rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)]"
            style={{ boxShadow: 'var(--co-shadow-card)' }}
          >
            {isLoadingDocuments ? (
              <div className="flex items-center justify-center p-12">
                <Spinner size="lg" />
              </div>
            ) : documentRows.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <h3 className="text-[15px] font-semibold text-[var(--co-ink)]">
                  No documents uploaded
                </h3>
                <p className="mx-auto mt-2 max-w-[56ch] text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">
                  A PDF here becomes a citable source: its text is extracted, embedded, and quoted
                  back with a page number. Until one exists the assistant can only answer from
                  articles.
                </p>
              </div>
            ) : (
              <div role="table" aria-label="Documents">
                <DocumentRegisterHead
                  allSelected={
                    selectedDocuments.length > 0 &&
                    selectedDocuments.length === documentRows.length
                  }
                  onSelectAll={handleSelectAllDocuments}
                />
                <div role="rowgroup">
                  {documentRows.map((row) => {
                    const doc = documentById.get(row.id);
                    return (
                      <DocumentRegisterRow
                        key={row.id}
                        row={row}
                        selected={selectedDocuments.includes(row.id)}
                        onToggle={(checked) => handleSelectDocument(row.id, checked)}
                        onOpen={() => doc && openDocumentInTab(doc)}
                        onDownload={() => doc && downloadDocument(doc)}
                        onDelete={() => handleDeleteDocument(row.id)}
                        categoryControl={
                          <Select
                            value={doc?.categoryId || ''}
                            onChange={(e) => handleChangeDocumentCategory(row.id, e.target.value)}
                            placeholder="No category"
                            options={[
                              { value: '', label: 'No category' },
                              ...categories.map((c) => ({ value: c.id, label: c.name })),
                            ]}
                          />
                        }
                      />
                    );
                  })}
                </div>
                {/* What the pipeline still does not report, where the person
                    who can authorise it will see it. */}
                <CardFoot
                  left={`All ${documentRows.length} documents shown`}
                  note="Per-page extraction progress is not reported, so a file still being processed shows no percentage."
                />
              </div>
            )}
          </div>
        </>
      ) : activeTab === 'search' ? (
        /* 18d. The title and the "answers drawn from your articles and
           documents" line are in the shared header now, so the panel starts
           at the question instead of repeating them. */
        <div
          className="rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] p-6"
          style={{ boxShadow: 'var(--co-shadow-card)' }}
        >
          <div className="mx-auto max-w-3xl">
            <RagSearch
              onArticleClick={(articleId) => navigate(`/knowledge/${articleId}`)}
              showSuggestions
            />
          </div>
        </div>
      ) : activeTab === 'analytics' ? (
        /* Insights IS the gap queue (phase 25 §4), not a charts page — §9
           deletes "search analytics as a chart with no action attached". What
           the old Knowledge Analytics screen held that was worth keeping was
           never the charts; it was the figures under them, and those are in
           InsightsStats below, drawn from the same payload as the band. */
        <>
          {/* Header and section strip share one card. They were the last two
              surfaces on this page sitting bare on the canvas, which made the
              panels below them look like they belonged to a different screen.
              The period selector is shown only on sections whose requests
              carry it (Bug 9308) — elsewhere it would claim a filter that is
              not applied. */}
          <div
            className="rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-5 pb-[14px] pt-[18px]"
            style={{ boxShadow: 'var(--co-shadow-card)' }}
          >
            <InsightsHeader
              days={insightsDays}
              onDaysChange={setInsightsDays}
              onRefresh={loadGaps}
              refreshing={isRefreshingInsights}
              showPeriod={sectionUsesPeriod(insightsSection)}
            />
            <div className="mt-[14px] border-t border-[var(--co-line-subtle)] pt-[14px]">
              <InsightsSectionTabs active={insightsSection} onSelect={setInsightsSection} />
            </div>
          </div>

          {insightsSection === 'overview' && (
            <>
              {/* Everything the retired Knowledge Analytics overview reported,
                  minus the four figures already in the gap band. No extra
                  request: it is the dashboard the gap queue already holds. */}
              <InsightsStats dashboard={gapDashboard} />
              {/* 18c. The artboard's two panels assume search queries are not
                  logged; here they are, so these JOIN the gap queue instead of
                  replacing it. Demand in Gaps, supply here. */}
              <CoveragePanels
                rows={articleRows}
                tree={categoryTree}
                categoryCount={categories.length}
                onWriteFor={(categoryId) => navigate(`/knowledge/new?categoryId=${categoryId}`)}
                onOpenArticle={(articleId) => navigate(`/knowledge/${articleId}`)}
              />
            </>
          )}

          {insightsSection === 'search' && (
            <SearchAnalyticsSection
              popular={popularSearches ?? []}
              failed={failedSearches ?? []}
              onDraft={(query) =>
                navigate(`/knowledge/new?topic=${encodeURIComponent(query.slice(0, 120))}`)
              }
            />
          )}

          {insightsSection === 'articles' && (
            <ArticlePerformanceSection
              articles={articleStats ?? []}
              onOpen={(articleId) => navigate(`/knowledge/${articleId}`)}
            />
          )}

          {insightsSection === 'gaps' && (
            <GapQueue
              dashboard={gapDashboard}
              gaps={gapRows}
              days={insightsDays}
              error={gapError}
              onDraft={(gap) =>
                navigate(`/knowledge/new?topic=${encodeURIComponent(gap.topic.slice(0, 120))}`)
              }
              onExtend={(_gap, articleId) => navigate(`/knowledge/${articleId}`)}
              onDismiss={handleDismissGap}
              hasMore={gapHasMore}
              onLoadMore={loadMoreGaps}
              loadingMore={loadingMoreGaps}
            />
          )}

          {insightsSection === 'embeddings' && (
            <EmbeddingsSection
              statuses={embedAll ?? []}
              onReindex={handleReindex}
              reindexing={isReindexing}
            />
          )}
        </>
      ) : null}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowCategoryModal(false)}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {editingCategory ? 'Edit Category' : 'New Category'}
              </h3>
              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, description: e.target.value })
                    }
                    className="input w-full"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Parent Category
                  </label>
                  <ThemedSelect
                    value={categoryForm.parentId}
                    onChange={(e) => setCategoryForm({ ...categoryForm, parentId: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">None (Top Level)</option>
                    {categories
                      .filter((c) => c.id !== editingCategory?.id)
                      .map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                  </ThemedSelect>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Icon
                  </label>
                  <div className="relative">
                    <ThemedSelect
                      value={categoryForm.icon}
                      onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                      className="input w-full pl-10"
                    >
                      <option value="">No icon</option>
                      {CATEGORY_ICONS.map((iconOption) => (
                        <option key={iconOption.name} value={iconOption.name}>
                          {iconOption.label}
                        </option>
                      ))}
                    </ThemedSelect>
                    {/* Preview selected icon */}
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      {categoryForm.icon ? (
                        (() => {
                          const iconConfig = CATEGORY_ICONS.find(i => i.name === categoryForm.icon);
                          if (iconConfig) {
                            const IconComponent = iconConfig.icon;
                            return <IconComponent className="h-4 w-4 text-gray-500 dark:text-gray-400" />;
                          }
                          return <Folder className="h-4 w-4 text-gray-400 dark:text-gray-500" />;
                        })()
                      ) : (
                        <Folder className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={categoryForm.isPublic}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, isPublic: e.target.checked })
                    }
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                  />
                  <label
                    htmlFor="isPublic"
                    className="text-sm text-gray-700 dark:text-gray-300"
                  >
                    Visible in public help center
                  </label>
                </div>
                <div className="flex items-center justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCategoryModal(false)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSaving}>
                    {isSaving ? <Spinner size="sm" /> : editingCategory ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
